/* eslint-disable max-lines */
import styled, { css } from "styled-components";
import {
  Actions,
  Blank,
  Chip,
  ChipContainer,
  Divider,
  FlatButton,
  Flex,
  Icon,
  IconButton,
  IconButtonButton,
  IconButtonInnerChild,
  InputsGroupContainer,
  JSONEditorContainer,
  JSONEditorWrapper,
  textOverflowEllipsisMixin,
} from "@evergis/ui";

import { LegendToggler } from "../LayerList/styled";

export const PanelEditorContainer = styled(Flex)`
  flex-direction: column;
  height: calc(100% - 1.5rem);
  padding: 0 1.5rem 1.5rem;

  ${JSONEditorContainer} {
    flex: 1;
  }

  ${JSONEditorWrapper} {
    width: 100%;
  }

  ${Actions} {
    margin-top: 0;
  }

  .jsoneditor,
  .ace-sqlserver {
    border: thin solid ${({ theme: { palette } }) => palette.elementDeep};
  }

  .ace_marker-layer {
    .ace_active-line,
    .ace_selection {
      background-color: ${({ theme: { palette } }) => palette.elementDark};
    }
  }

  .ace_keyword,
  .ace_variable {
    color: ${({ theme: { palette } }) => palette.primary};
  }

  .ace_string {
    color: ${({ theme: { palette } }) => palette.secondary};
  }

  .ace_invalid_constant {
    color: ${({ theme: { palette } }) => palette.error};
  }

  .ace_line,
  .ace_identifier,
  .ace_constant.ace_numeric {
    color: ${({ theme: { palette } }) => palette.textPrimary};
  }

  .ace_gutter,
  .ace_gutter-active-line {
    background: transparent;
  }
`;

export const EditorWrapper = styled(Flex)`
  flex-grow: 1;
  width: 100%;

  > * {
    flex-grow: 1;
    height: 100%;
  }
`;

export const ConfigurationsHeaderContainer = styled(Flex)`
  justify-content: space-between;
  padding: 1.5rem;
`;

export const ConfigurationsHeaderTitle = styled.div`
  width: 100%;
  flex: 1;
`;

export const ConfigurationsHeaderAlias = styled(Flex)`
  align-items: center;
  font-size: 1.5rem;
  font-family: "Roboto Light", sans-serif;
  color: ${({ theme: { palette } }) => palette.textPrimary};
  ${textOverflowEllipsisMixin}
`;

export const ConfigurationsHeaderType = styled(Flex)`
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};

  ${Icon} {
    margin-right: 0.5rem;
  }
`;

export const ConfigurationsHeaderButtons = styled(Flex)`
  align-items: center;
`;

export const ConfigurationsNavigation = styled(Flex)`
  padding: 0.5rem 1.5rem 0;

  > * {
    cursor: pointer;
    margin-bottom: 0.5rem;

    :not(:last-child) {
      margin-right: 0.5rem;
    }
  }
`;

export const ConfigurationsHeaderDivider = styled.div`
  height: 1rem;
  width: 1px;
  margin: 0 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.elementDeep};
`;

export const ConfigurationsDescr = styled.div`
  color: ${({ theme: { palette } }) => palette.textDisabled};
`;

export const ConfigurationsReference = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
  padding: 0.5rem 1.5rem 0;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const ConfigurationsEditorHelp = styled(IconButton)`
  font-size: 0.75rem;
  line-height: 0;

  ${IconButtonButton} {
    margin-right: -0.125rem;

    span[kind]:after {
      font-size: 0.875rem;
    }
  }
`;
ConfigurationsEditorHelp.defaultProps = {
  kind: "question",
  primary: true,
};

export const ConfigurationsToolbar = styled(Flex)`
  margin: 0 1.5rem 0.5rem;
`;

export const DashboardPages = styled(Flex)`
  ${ChipContainer} {
    cursor: pointer;
    display: flex;
    align-items: center;
    height: 0.875rem;
    margin: 0 0.25rem 0.25rem 0;
    border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};
    line-height: 1;
    font-size: 0.75rem;

    > * {
      color: ${({ theme: { palette } }) => palette.textSecondary};
    }
  }

  span[kind] {
    :after {
      color: ${({ theme: { palette } }) => palette.textSecondary};
    }

    &[kind="feature_add"] {
      margin-right: 0.25rem;
    }
  }
`;

export const DashboardPage = styled(Chip)<{ active?: boolean }>`
  color: ${({ theme: { palette } }) => palette.textSecondary};

  ${Divider} {
    height: 1.375rem;
    margin-top: -0.25rem;
  }

  ${({ active }) =>
    active &&
    css`
      background-color: rgba(0, 170, 255, 0.06);
      color: ${({ theme: { palette } }) => palette.primary};

      && > * {
        color: ${({ theme: { palette } }) => palette.primary};
      }

      span[kind]:after {
        color: ${({ theme: { palette } }) => palette.primary};
      }
    `};
`;

export const DashboardPageTitle = styled.div`
  margin: 0 0.5rem;
`;

export const ConfigurationsInfo = styled(Flex)`
  justify-content: space-between;
  min-height: 4.375rem;
  margin-top: 0.5rem;
  padding: 0 1.5rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  color: ${({ theme: { palette } }) => palette.textPrimary};

  > * {
    flex: 1;
  }
`;

export const ConfigurationsFooter = styled(Flex)<{ right?: boolean }>`
  justify-content: ${({ right }) => (right ? "flex-end" : "space-between")};
  width: 100%;
  margin-top: 0.75rem;
  font-size: 0.875rem;
`;

export const EqlParameterContainer = styled(Flex)<{ isAction?: boolean }>`
  margin: 0.25rem 0.25rem 0 0;

  ${ChipContainer} {
    font-family: "Roboto Bold", sans-serif;
    font-size: 0.625rem;
    border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
    color: rgba(17, 174, 26);

    background-color: rgba(17, 174, 26, 0.06);

    ${IconButtonButton} {
      font-family: "Roboto Bold", sans-serif;
      font-size: 0.625rem;
      ${({ isAction }) =>
        isAction
          ? css`
              height: 1.25rem;
              padding: 0.25rem 0.5rem;
              border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
            `
          : css`
              height: 0.75rem;
              padding-left: 0.25rem;
              padding-right: 0.25rem;
            `}

      &:first-child {
        border-top-left-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
        border-bottom-left-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
      }

      &:last-child {
        border-top-right-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
        border-bottom-right-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
      }

      &:active {
        background-color: rgba(0, 117, 7, 0.28) !important;
      }

      &:hover {
        background-color: rgba(6, 150, 14, 0.28);
      }

      ${IconButtonInnerChild} {
        color: rgba(17, 174, 26);
      }

      span[kind] {
        width: 0.75rem;
        height: 0.75rem;

        color: rgba(17, 174, 26);

        :after {
          font-size: 0.75rem;
        }
      }
    }
  }
`;

export const EqlChip = styled(Chip)`
  padding: 0;
`;

export const EqlParameterBlank = styled(Blank)`
  padding: 1rem;

  ${InputsGroupContainer} {
    margin-bottom: 0.5rem;
  }

  ${LegendToggler} {
    margin: 0.5rem 0;
  }
`;

export const EqlParameter = styled(Flex)`
  button {
    width: auto;
    color: rgba(17, 174, 26);
  }
`;

export const ParameterDivider = styled(Divider)`
  border-color: rgba(17, 174, 26);
  opacity: 0.36;
`;

ParameterDivider.defaultProps = {
  vertical: true,
};

export const IconButtons = styled(Flex)`
  margin-top: 0.5rem;
  justify-content: center;
`;

export const EmptyContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

export const EmptyImage = styled.div`
  margin-bottom: 0.75rem;
  opacity: 0.12;
`;

export const EmptyText = styled.div`
  max-width: 12.5rem;
  margin-bottom: 0.75rem;
  text-align: center;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textDisabled};
`;

export const EmptyButton = styled(FlatButton)`
  background-color: transparent;
`;
EmptyButton.defaultProps = { primary: true };

export const DeleteButton = styled(IconButton)`
  && {
    margin: 0 -1rem 0 -0.25rem;
  }
`;
DeleteButton.defaultProps = { kind: "delete" };

export const MenuButton = styled(IconButton)`
  && {
    margin: 0 -0.25rem 0 -0.75rem;
  }
`;
MenuButton.defaultProps = { kind: "list" };
